import React from 'react';
import { ArrowRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

// Enhanced navigation structure
const navigation = {
  solutions: [
    { name: 'Model Portfolios', href: '/models' },
    { name: 'Power List™', href: '/power-list' },
    { name: 'Analytics Platform', href: '/#models' },
    { name: 'Custom Strategies', href: '/#contact' },
  ],
  resources: [
    { name: 'Methodology', href: '/methodology' },
    { name: 'Research', href: '/research' },
    { name: 'Platform Guide', href: '/platform-guide' },
    { name: 'Documentation', href: '/#contact' },
  ],
  company: [
    { name: 'About Us', href: '/#about' },
    { name: 'Team', href: '/team' },
    { name: 'Careers', href: '/#contact' },
    { name: 'Contact', href: '/#contact' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Disclaimers', href: '#disclaimer' },
    { name: 'Compliance', href: '/#contact' },
  ],
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer aria-labelledby="footer-heading" className="bg-gradient-to-b from-bg-200 to-bg-300">
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      
      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-6 pt-20 pb-12 lg:px-8 lg:pt-24">
        {/* Top Section - Brand and CTA */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center mb-16">
          <div>
            <img alt="SharpeMetrix" src="/logo_wordmark.svg" className="h-8 mb-6" />
            <h3 className="text-2xl font-bold text-text-100 mb-4">
              Institutional-Grade Investment Solutions
            </h3>
            <p className="text-lg text-text-200 max-w-md">
              Empowering financial advisors with proprietary alternative data signals and sophisticated quantitative modeling.
            </p>
          </div>
          
          <div className="mt-10 lg:mt-0">
            <div className="bg-primary-100/10 rounded-2xl p-8 ring-1 ring-primary-100/20">
              <h4 className="text-xl font-semibold text-text-100 mb-4">Ready to Get Started?</h4>
              <p className="text-text-200 mb-6">
                Discover how our systematic approach can enhance your investment strategy.
              </p>
              <a
                href="/#contact"
                className="inline-flex items-center gap-x-3 rounded-lg bg-primary-100 px-6 py-3 text-base font-semibold text-white shadow-lg hover:bg-primary-200 transition-all duration-300 hover:shadow-xl hover:shadow-primary-100/25"
              >
                Contact Us Today
                <ArrowRightIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-4 lg:gap-12 mb-16">
          <div>
            <h3 className="text-sm font-bold leading-6 text-text-100 uppercase tracking-wider mb-6">Solutions</h3>
            <ul className="space-y-4">
              {navigation.solutions.map((item) => (
                <li key={item.name}>
                  <a 
                    href={item.href} 
                    className="text-sm leading-6 text-text-200 hover:text-primary-100 transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-sm font-bold leading-6 text-text-100 uppercase tracking-wider mb-6">Resources</h3>
            <ul className="space-y-4">
              {navigation.resources.map((item) => (
                <li key={item.name}>
                  <a 
                    href={item.href} 
                    className="text-sm leading-6 text-text-200 hover:text-primary-100 transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-sm font-bold leading-6 text-text-100 uppercase tracking-wider mb-6">Company</h3>
            <ul className="space-y-4">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <a 
                    href={item.href} 
                    className="text-sm leading-6 text-text-200 hover:text-primary-100 transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-sm font-bold leading-6 text-text-100 uppercase tracking-wider mb-6">Legal</h3>
            <ul className="space-y-4">
              {navigation.legal.map((item) => (
                <li key={item.name}>
                  <a 
                    href={item.href} 
                    className="text-sm leading-6 text-text-200 hover:text-primary-100 transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-text-100/10 pt-12 mb-12">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <h4 className="text-lg font-semibold text-text-100 mb-4">Professional Use Only</h4>
              <p className="text-sm text-text-200 leading-6">
                SharpeMetrix and all of its content is for <strong>Investment Professional Use Only</strong>. 
                SharpeMetrix is an Internet-based publication, not an investment advisor or Broker/Dealer. 
                It is not registered as an investment adviser with any regulatory agency.
              </p>
            </div>
            
            <div className="mt-8 lg:mt-0">
              <h4 className="text-lg font-semibold text-text-100 mb-4">Get in Touch</h4>
              <div className="space-y-3">
                <a 
                  href="mailto:info@sharpemetrix.com" 
                  className="flex items-center text-sm text-text-200 hover:text-primary-100 transition-colors"
                >
                  <EnvelopeIcon className="h-5 w-5 mr-3" />
                  info@sharpemetrix.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Copyright and Disclaimer */}
        <div className="border-t border-text-100/10 pt-8">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-8">
              <p className="text-sm text-text-200">
                © {currentYear} SharpeMetrix. All rights reserved.
              </p>
              <p className="text-xs text-text-300 mt-2 lg:mt-0">
                Built for Investment Professionals • Institutional Quality, Advisor Focused
              </p>
            </div>
            
            <div className="mt-4 lg:mt-0">
              <p className="text-xs text-text-300 text-right">
                Securities offered through registered representatives
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Integrated Disclaimer Section */}
      <div id="disclaimer" className="bg-bg-300 border-t border-text-100/10">
        <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
          <div className="space-y-6 text-xs text-text-300 leading-5">
            <div>
              <h5 className="font-semibold text-text-200 mb-2">Important Disclosure Regarding Investment Models:</h5>
              <p>
                Investment models, including SharpeMetrix's, are for informational purposes only. Past performance doesn't predict future results. 
                Models have limitations and may overlook events, economic changes, or other factors impacting investments. Advisors must consider 
                investors' goals, risk tolerance, and time horizon. Models may not capture complexities of individual portfolios. Investment 
                decisions require thorough examination of multiple factors. No model guarantees market outperformance. Conduct comprehensive 
                analysis before making investment decisions. Always consult with a qualified financial advisor.
              </p>
            </div>
            
            <div>
              <p>
                SharpeMetrix is an independent entity and is not affiliated with, nor does it have any sponsorship or endorsement relationships 
                with, any annuity, mutual fund or exchange-traded fund (ETF) sponsors or their affiliates. Our investment models, research, and 
                analyses are developed independently, without influence from any annuity, mutual fund or ETF sponsors or their affiliates. Our 
                focus is on providing objective and independent market insights to assist our members in making informed investment decisions for their clients.
              </p>
            </div>
            
            <div>
              <p>
                The Site offers impersonal investment models without personalized advice. Past performance does not predict future results. 
                All investments involve risk, including the potential loss of principal. Consult with a qualified financial advisor before 
                making any investment decisions. SharpeMetrix relies on the 'publisher's exclusion' from the definition of 'investment adviser' 
                under Section 202(a)(11) of the Investment Advisers Act of 1940 and state securities laws.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
} 